import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  GetAllEventsByOrganiserId,
  AddEvent,
  UpdateEvent,
  GetAllUsers,
  GetAllEventTypes,
} from '../../api/api';
import { useCrudList } from '../../utils/useCrudList';
import { EVENT_TYPES, fmtDate } from '../../utils/helpers';

// Events are the top of the planning tree. Super Admin and Admin users can
// create them; functions, members, and guests live inside a selected event.
export const columns = [
  { key: 'EventName', label: 'Event' },
  { key: 'EventType', label: 'Type', render: r => r.EventTypeName || r.EventType || '-' },
  { key: 'EventAdminName', label: 'Event Admin', render: r => [r.EventAdminName, r.EventAdminMobile].filter(Boolean).join(' | ') || '-' },
  { key: 'EventStartDate', label: 'Start', render: r => fmtDate(r.EventStartDate) },
  { key: 'EventEndDate', label: 'End', render: r => fmtDate(r.EventEndDate) },
  { key: 'Venue', label: 'Venue' },
  { key: 'City', label: 'City' },
  { key: 'Status', label: 'Status', render: r => <span className="chip pend">{r.Status}</span> },
];

export const fields = [
  { key: 'EventName', label: 'Event Name', live: true, required: true },
  { key: 'EventTypeId', label: 'Event Type', type: 'select', options: EVENT_TYPES, required: true },
  { key: 'EventAdminMode', label: 'Event Admin', type: 'select', options: ['Existing User', 'New User'], default: 'Existing User' },
  { key: 'EventAdminUserId', label: 'Existing User', type: 'select' },
  { key: 'EventAdminName', label: 'New User Name', live: true },
  { key: 'EventAdminMobileNumber', label: 'New User Mobile' },
  { key: 'EventAdminPassword', label: 'New User Password', default: '1234' },
  { key: 'Status', label: 'Status', type: 'select', options: ['Planning', 'Active', 'Completed', 'Cancelled'], default: 'Planning', half: true },
  // Both ends of an event are required - every function date sits inside them.
  { key: 'EventStartDate', label: 'Start Date', type: 'date', required: true, half: true },
  { key: 'EventEndDate', label: 'End Date', type: 'date', required: true, half: true },
  { key: 'Venue', label: 'Venue', live: true, half: true },
  { key: 'City', label: 'City', live: true, half: true },
  { key: 'MaleSideName', label: 'Primary Side Name', live: true, half: true },
  { key: 'FemaleSideName', label: 'Secondary Side Name', live: true, half: true },
  { key: 'VenueAddress', label: 'Venue Address', type: 'textarea' },
  { key: 'Description', label: 'Description', type: 'textarea' },
];

const toOptions = (rows) => rows.map(row => ({
  value: row.UserId,
  label: [row.Surname, row.Name, row.MobileNumber ? `(${row.MobileNumber})` : ''].filter(Boolean).join(' '),
}));

const cleanPayload = (values) => Object.fromEntries(
  Object.entries(values).map(([key, value]) => [key, value === '' ? null : value])
);

export function useEvents() {
  const { user, organiser, isSuperAdmin, selectEvent } = useAuth();
  const nav = useNavigate();
  // Row click = drill in: make it the current event and open its Functions (Esc comes back).
  const openRow = (row) => { selectEvent(row); nav('/app/functions'); };
  const [eventTypes, setEventTypes] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    GetAllEventTypes()
      .then(rows => setEventTypes(Array.isArray(rows) && rows.length ? rows : []))
      .catch(() => setEventTypes([]));
    GetAllUsers()
      .then(rows => setUsers(Array.isArray(rows) ? rows : []))
      .catch(() => setUsers([]));
  }, []);

  const eventTypeOptions = useMemo(() => (
    eventTypes.length
      ? eventTypes.map(row => ({ value: row.EventTypeId, label: row.EventTypeName }))
      : EVENT_TYPES.map(label => ({ value: label, label }))
  ), [eventTypes]);
  const userOptions = useMemo(() => toOptions(users), [users]);
  const formFields = useMemo(() => fields.map(field => {
    if (field.key === 'EventTypeId') return { ...field, options: eventTypeOptions };
    if (field.key === 'EventAdminUserId') return { ...field, options: userOptions };
    return field;
  }), [eventTypeOptions, userOptions]);

  const load = useCallback(
    () => GetAllEventsByOrganiserId({ OrganiserId: organiser.OrganiserId }),
    [organiser]
  );

  const onSave = async (values, editing) => {
    const payload = cleanPayload(values);
    payload.EventAdminMode = payload.EventAdminMode || 'Existing User';
    const selectedType = eventTypes.find(row => String(row.EventTypeId) === String(payload.EventTypeId));
    if (selectedType) payload.EventType = selectedType.EventTypeName;
    else if (payload.EventTypeId && !Number(payload.EventTypeId)) {
      payload.EventType = payload.EventTypeId;
      delete payload.EventTypeId;
    }
    if (payload.EventAdminMode === 'Existing User') {
      if (!payload.EventAdminUserId) throw new Error('Event Admin is required');
      delete payload.EventAdminName;
      delete payload.EventAdminMobileNumber;
      delete payload.EventAdminPassword;
    } else {
      if (!payload.EventAdminName || !payload.EventAdminMobileNumber) throw new Error('New Event Admin details are required');
      delete payload.EventAdminUserId;
    }
    delete payload.EventAdminMode;

    if (editing) {
      return UpdateEvent({ data: { ...payload, UpdatedBy: user.UserId }, where: `EventId = ${editing.EventId}` });
    }
    return AddEvent({
      ...payload,
      OrganiserId: organiser.OrganiserId,
      CreatedBy: user.UserId,
      UpdatedBy: user.UserId,
    });
  };

  const onDelete = (row) => {
    return UpdateEvent({ data: { Valid: 0, UpdatedBy: user.UserId }, where: `EventId = ${row.EventId}` });
  };

  const can = { add: isSuperAdmin, edit: isSuperAdmin, delete: isSuperAdmin };

  const vm = useCrudList({
    load, onSave, onDelete, fields: formFields, can,
    confirmDelete: (r) => `Delete event "${r.EventName || ''}"?`,
  });
  return { ...vm, columns, isSuperAdmin, eventTypes, users, openRow };
}
